import { useState, useRef, useEffect } from "react";
import { Camera, X, Check, AlertTriangle, MapPin, Shield } from "lucide-react";
import { Card } from "./Card";
import { Btn } from "./Btn";

/* SelfieCaptureModal — real getUserMedia() camera. Two-step flow:
   capture frame → enter location text. Explicitly never reads
   navigator.geolocation. Hands back (dataUrl, hhmm, locationText) on
   submit. */
export function SelfieCaptureModal({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [err, setErr] = useState(null);
  const [ready, setReady] = useState(false);
  const [capturedSelfie, setCapturedSelfie] = useState(null);
  const [capturedTime, setCapturedTime] = useState(null);
  const [location, setLocation] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoFailed, setGeoFailed] = useState(false);
  const [geoErr, setGeoErr] = useState("");
  // Reverse-geocode coords -> readable address (Nominatim, no key).
  async function reverseGeocode(latitude, longitude) {
    const coords = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=16&addressdetails=1`,
        { headers: { "Accept": "application/json" } }
      );
      const data = await res.json();
      setLocation(data?.display_name || coords); setLocErr(false);
    } catch {
      setLocation(coords); setLocErr(false);
    } finally { setGeoLoading(false); }
  }
  // Two-tier: high-accuracy first, then low-accuracy (WiFi/network) fallback.
  function fetchLocation() {
    setGeoFailed(false); setGeoErr("");
    if (!("geolocation" in navigator)) { setGeoFailed(true); setGeoErr("This device does not support location."); return; }
    setGeoLoading(true);
    const onOk = (pos) => reverseGeocode(pos.coords.latitude, pos.coords.longitude);
    const reason = (e) => e && e.code === 1 ? "Location permission denied. Enable it in Settings → Apps → AbhiGhar → Permissions."
      : e && e.code === 2 ? "Location services are OFF. Please turn on Location in your phone Settings and retry."
      : e && e.code === 3 ? "Location timed out. Make sure Location services are ON in phone Settings, then retry."
      : "Could not get your location. Ensure Location services are ON in phone Settings.";
    const tryLow = () => navigator.geolocation.getCurrentPosition(
      onOk,
      (e) => { setGeoLoading(false); setGeoFailed(true); setGeoErr(reason(e)); },
      { enableHighAccuracy: false, timeout: 18000, maximumAge: 60000 }
    );
    navigator.geolocation.getCurrentPosition(
      onOk,
      () => tryLow(),
      { enableHighAccuracy: true, timeout: 18000, maximumAge: 60000 }
    );
  }
  const [locErr, setLocErr] = useState(false);

  useEffect(() => {
    if (!open) {
      setCapturedSelfie(null); setCapturedTime(null); setLocation(""); setLocErr(false);
      return;
    }
    let cancelled = false;
    setErr(null); setReady(false);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (e) {
        setErr(e.message || "Camera access denied");
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 480;
    c.height = v.videoHeight || 360;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const data = c.toDataURL("image/jpeg", 0.7);
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    setCapturedSelfie(data);
    setCapturedTime(hhmm);
    fetchLocation();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const submit = () => {
    if (!location.trim()) { setLocErr(true); return; }
    onCapture(capturedSelfie, capturedTime, location.trim());
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Camera className="w-4 h-4" />
            {capturedSelfie ? "Confirm location" : "Mark present — take a selfie"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        {err ? (
          <div className="p-4 rounded-lg bg-red-50 ring-1 ring-inset ring-red-600/20 text-sm text-red-800">
            <div className="font-medium mb-1 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Camera unavailable</div>
            <p className="text-xs leading-relaxed">{err}. Allow camera access in your browser settings and try again.</p>
          </div>
        ) : !capturedSelfie ? (
          <>
            <div className="rounded-lg overflow-hidden bg-slate-900 aspect-[4/3] grid place-items-center">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
            <p className="text-xs text-slate-500 mt-3 flex items-start gap-1.5">
              <Shield className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
              A selfie and your current location are captured. Allow location access when prompted.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
              <Btn variant="primary" onClick={capture} className={!ready ? "opacity-50 pointer-events-none" : ""}>
                <Camera className="w-4 h-4" />Capture
              </Btn>
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-3 items-start mb-4">
              <img src={capturedSelfie} alt="Captured selfie" className="w-24 h-24 rounded-lg object-cover ring-1 ring-slate-200 shrink-0" />
              <div className="text-xs text-slate-600 leading-relaxed">
                <div className="font-medium text-slate-900 mb-0.5">Selfie captured at {capturedTime}</div>
                {geoLoading ? "Fetching your location…" : "Your current location was captured automatically."}
              </div>
            </div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">
              Location {geoLoading && <span className="text-slate-400 font-normal">(detecting…)</span>}
            </label>
            <div className={`w-full text-sm rounded-lg ring-1 ring-inset px-3 py-2 min-h-[38px] flex items-center ${locErr ? "ring-red-400 bg-red-50/30" : "ring-slate-300 bg-slate-50"}`}>
              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mr-2" />
              <span className="text-slate-700 leading-snug">{geoLoading ? "Detecting your location…" : (location || "Location unavailable")}</span>
            </div>
            {geoFailed && (
              <div className="mt-2">
                <p className="text-xs text-amber-600 mb-1.5">{geoErr || 'Location not detected.'} Enter it manually or retry.</p>
                <input value={location} onChange={(e) => { setLocation(e.target.value); setLocErr(false); }}
                  placeholder="e.g. AbhiGhar office · Baner, Pune"
                  className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-blue-500" />
                <button type="button" onClick={fetchLocation} className="text-xs text-blue-600 hover:underline mt-1.5">Retry auto-detect</button>
              </div>
            )}
            {locErr && <p className="text-xs text-red-600 mt-1.5">Location is required — allow location access or enter manually.</p>}
            <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
              Your device location is captured automatically and stored with this attendance record.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Btn variant="ghost" onClick={() => { setCapturedSelfie(null); setCapturedTime(null); }}>Retake selfie</Btn>
              <Btn variant="primary" onClick={submit}><Check className="w-4 h-4" />Submit attendance</Btn>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
